import { Combobox } from "design-system/organisms/Combobox"
import { useDebouncedValue } from "modules/shared/hooks/useDebouncedValue"
import { ReactElement, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { OrganizationTypeEnum } from "schema"
import { useGetBackofficeOrganizationsQuery } from "../hooks/organizations/useGetBackofficeOrganizationsQuery"

type Option = {
  value: string
  label: string | ReactElement
  muted?: boolean
}

type OrganizationSelectorProps = {
  value: string | undefined
  onChange: (organizationId: string | undefined) => void
  className?: string
}

export const OrganizationSelector = ({ value, onChange, className }: OrganizationSelectorProps) => {
  const { t } = useTranslation()
  const [searchQuery, setSearchQuery] = useState("")
  const debouncedSearchQuery = useDebouncedValue({
    value: searchQuery,
  })

  const { data: organizations, isPending } = useGetBackofficeOrganizationsQuery({
    query: {
      query: debouncedSearchQuery || undefined,
      page: 1,
      total: 100,
      filterBy: {
        type: [OrganizationTypeEnum.Client, OrganizationTypeEnum.Partner],
      },
    },
  })

  const options = useMemo(() => {
    const array: Option[] = [
      {
        label: t("backoffice.companies.companyCreator.section.organization.fields.new"),
        value: "__new__",
        muted: true,
      },
    ]

    if (organizations) {
      array.push(
        ...organizations.map((org) => ({
          label: org.name,
          value: org.id,
        })),
      )
    }

    return array
  }, [organizations, t])

  const selectedOption = useMemo(() => {
    if (!value || value === "__new__") {
      return options[0]
    }
    return options.find((option) => option.value === value)
  }, [value, options])

  function onComboboxChange(option: Option | undefined) {
    const newValue = option?.value === "__new__" ? undefined : option?.value
    onChange(newValue)
  }

  return (
    <Combobox
      options={options}
      value={selectedOption}
      onChange={onComboboxChange}
      placeholder={t("backoffice.companies.companyCreator.section.organization.fields.placeholder")}
      emptyPlaceholder={t(
        "backoffice.companies.companyCreator.section.organization.fields.noResults",
      )}
      searchPlaceholder={t(
        "backoffice.companies.companyCreator.section.organization.fields.search",
      )}
      disabled={isPending}
      searchQuery={searchQuery}
      setSearchQuery={setSearchQuery}
      className={className}
      shouldFilter={false}
    />
  )
}
